import { Select } from '@dev-spendesk/grapes';

import { useTranslation } from 'common/hooks/useTranslation';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import {
  getBudgetLimit,
  getExceededAmount,
  getUsedAmount,
  hasExceededAmounts,
} from '../../budgets/models/breakdown';
import { type Trip } from '../../trips/hooks/useAllTripsQuery';
import { getTripDates } from '../utils/trips';

type SelectOption = {
  key: string;
  label: string;
  destination: string | null;
  dates: {
    from: Date;
    to: Date;
  };
  budget?: {
    state: NonNullable<NonNullable<Trip['budget']>['state']>;
  };
};

type Props = {
  placeholder?: string;
  options: SelectOption[];
  value: SelectOption | undefined;
  onSelect: (value: SelectOption) => void;
  renderNoOptions: () => React.ReactNode;
};

export const TripSelect = ({
  placeholder,
  options,
  value,
  onSelect,
  renderNoOptions,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  return (
    <Select
      fit="parent"
      placeholder={placeholder}
      options={options}
      value={value}
      onSelect={onSelect}
      renderOption={(option) => (
        <div className="flex cursor-pointer justify-between px-4 py-8">
          <div className="flex flex-col">
            <span>{option.destination ?? ''}</span>
            <span className="body-s text-content-secondary-bg-secondary">
              {getTripDates(option.dates.from, option.dates.to, localeFormat)}
            </span>
          </div>
          {option.budget && (
            <div className="flex flex-col text-end">
              {hasExceededAmounts(option.budget.state) ? (
                <span className="text-content-warning-default">
                  {t('requests.panel.travelBudget.tripSelect.overBudget', {
                    budget: formatMonetaryValue(
                      getExceededAmount(option.budget.state),
                    ),
                  })}
                </span>
              ) : (
                <span>
                  {t('requests.panel.travelBudget.tripSelect.remainingBudget', {
                    budget: formatMonetaryValue(option.budget.state.available),
                  })}
                </span>
              )}
              <span className="body-s text-content-secondary-bg-secondary">
                {formatMonetaryValue(getUsedAmount(option.budget.state))}/
                {formatMonetaryValue(getBudgetLimit(option.budget.state))}
              </span>
            </div>
          )}
        </div>
      )}
      renderNoOptions={renderNoOptions}
    />
  );
};
